export interface TimelineFeedItem {
  id: string | number;
  title: string;
  headline?: string;
  source: string;
  publishedAt: string | Date;
  sentiment?: string;
  macroLevel?: number;
  tags?: string[];
  narrativeThreads?: string[];
  ivScore?: number;
  priceBrainScore?: {
    sentiment?: string;
    impliedPoints?: number | null;
    instrument?: string | null;
  };
}

export const TIMELINE_THREADS = [
  { slug: "all", title: "All Headlines", color: "var(--fintheon-accent)" },
  {
    slug: "middle-east-conflict",
    title: "Middle Eastern Conflict",
    color: "#F59E0B",
  },
  {
    slug: "liquidity-credit-contraction",
    title: "Liquidity & Credit",
    color: "#8B5CF6",
  },
  { slug: "ai-singularity", title: "The Singularity", color: "#3B82F6" },
  {
    slug: "usd-jpy-carry-trade",
    title: "USD-JPY Carry Trade",
    color: "#EC4899",
  },
  { slug: "trade-war", title: "Trade War", color: "#EF4444" },
  { slug: "us-china-relations", title: "US-China Relations", color: "#14B8A6" },
  { slug: "rate-cut-cycle", title: "Rate Cut Cycle", color: "#34D399" },
  { slug: "trump-presidency", title: "Trump Presidency", color: "#F97316" },
  { slug: "price-stability", title: "Price Stability", color: "#FBBF24" },
  { slug: "maximum-employment", title: "Max Employment", color: "#A78BFA" },
] as const;

export const TIMELINE_RANGES = [
  { key: "1h", label: "1H" },
  { key: "4h", label: "4H" },
  { key: "1d", label: "1D" },
  { key: "1w", label: "1W" },
  { key: "all", label: "ALL" },
] as const;

export const TIMELINE_MACRO_LEVELS = [
  { level: 4, label: "Critical", color: "var(--fintheon-severe)" },
  { level: 3, label: "High", color: "var(--fintheon-high)" },
  { level: 2, label: "Medium", color: "var(--fintheon-neutral-severe)" },
  { level: 1, label: "Low", color: "var(--fintheon-low)" },
] as const;

export function getTimelineCutoff(range: string): Date | null {
  if (range === "all") return null;
  const milliseconds: Record<string, number> = {
    "1h": 3_600_000,
    "4h": 14_400_000,
    "1d": 86_400_000,
    "1w": 604_800_000,
  };
  return new Date(Date.now() - (milliseconds[range] ?? 0));
}

export function groupTimelineByDate(
  items: TimelineFeedItem[],
): [string, TimelineFeedItem[]][] {
  const groups = new Map<string, TimelineFeedItem[]>();
  for (const item of items) {
    const publishedAt =
      item.publishedAt instanceof Date
        ? item.publishedAt.toISOString()
        : item.publishedAt;
    const key = publishedAt.slice(0, 10) || "unknown";
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

export function formatTimelineDate(value: string | Date): string {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function formatTimelineRelative(value: string | Date): string {
  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function timelineMacroColor(level = 2): string {
  return (
    TIMELINE_MACRO_LEVELS.find((item) => item.level === level)?.color ??
    "var(--fintheon-neutral-severe)"
  );
}
